import { useEffect, useState } from "react";
import { FileText, Loader2, Pencil, ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { WhatsAppGlyph } from "@/components/cards/CardShell";
import { STOCK, packPay, resell, makeProforma, agentCartProforma, proformaParts, validKey, campCount, campMissing, campPrice, campRule, giftText, ron } from "@/lib/bf";
import { waLink } from "@/lib/wa";
import BFNotes, { noteLines, numbered } from "./BFNotes";
import BFProfit from "./BFProfit";

export default function Cart({ cfg, contact, cart, setQty, clear, onOrder, preview, edit, noMin }) {
  const [open, setOpen] = useState(false);
  const [pf, setPf] = useState(null);
  const [review, setReview] = useState(false);
  const [obs, setObs] = useState(() => (edit ? "" : localStorage.getItem("bf_notes") || ""));
  useEffect(() => { if (!edit) localStorage.setItem("bf_notes", obs); }, [obs, edit]);
  useEffect(() => { const on = () => setOpen(true); window.addEventListener("bf-open-cart", on); return () => window.removeEventListener("bf-open-cart", on); }, []);
  const sig = JSON.stringify(cart);
  useEffect(() => { if (!edit) setPf(null); setReview(false); }, [sig, edit]);
  const byId = Object.fromEntries(cfg.products.map((p) => [p.id, p]));
  const pIdx = Object.fromEntries(cfg.products.map((p, i) => [p.id, i]));
  const gIdx = Object.fromEntries((cfg.gamas || []).map((g, i) => [g.id, i]));
  const bIdx = Object.fromEntries((cfg.brands || []).map((b, i) => [b.id, i]));
  const sortKey = (p, sh) => {
    const lbl = sh ? (/^\d/.test(sh.name || "") ? sh.name : sh.code) : "";
    const n = (lbl.match(/\d+/g) || []).slice(0, 4).map(Number);
    return [bIdx[p.brand] ?? 99, gIdx[p.gama] ?? 1e5, pIdx[p.id], ...(sh ? (n.length ? [...n, ...Array(4 - n.length).fill(-1)] : [999, 0, 0, 0]) : [-1, -1, -1, -1])];
  };
  const cmpKey = (a, b) => { for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return a[i] - b[i]; return 0; };
  const asLine = (id, q) => {
    const [kind, cid, pid, ...rest] = id.split(":");
    const code = rest.join(":");
    const c = (cfg.campaigns || []).find((x) => x.id === cid);
    const p = byId[pid];
    if (kind !== "cp" || !c || !p || ![...c.items, ...(c.extras || [])].includes(pid)) return null;
    const sh = code && p.shades?.find((x) => x.code === code);
    if (!validKey(id, byId)) return null;
    return { id, q, sk: sortKey(p, sh), shade: !!sh, culoare: c.category === "culoare" && c.items.includes(pid) && !p.shades?.length, brand: p.brand, name: `${p.name}${sh ? ` · nuanța ${sh.code}${sh.name ? ` ${sh.name}` : ""}` : ""} · ${c.name} -${c.discount}%`, unit: campPrice(p, c), per: "buc", normal: Number(p.salon), retail: Number(p.retail), sell: resell(p.salon, c)?.sell || 0 };
  };
  const lines = Object.entries(cart).filter(([, q]) => q > 0).map(([id, q]) => asLine(id, q)).filter(Boolean).sort((a, b) => cmpKey(a.sk, b.sk));
  if (!lines.length) return null;
  const used = (cfg.campaigns || []).filter((c) => lines.some((l) => l.id.startsWith(`cp:${c.id}:`)));
  const unmet = noMin ? [] : used.map((c) => ({ c, missing: campMissing(c, campCount(c, cart, byId)) })).filter((x) => x.missing.length);
  const pieces = lines.reduce((s, l) => s + l.q, 0);
  const met = used.filter((c) => !campMissing(c, campCount(c, cart, byId)).length);
  const packs = met.filter((c) => c.surprise_value > 0).map((c) => ({ c, pay: packPay(c) }));
  const total = lines.reduce((s, l) => s + l.unit * l.q, 0) + packs.reduce((s, x) => s + x.pay, 0);
  const parts = Object.entries(lines.reduce((m, l) => {
    const k = l.id.split(":")[1];
    return { ...m, [k]: (m[k] || 0) + l.unit * l.q };
  }, Object.fromEntries(packs.map((x) => [x.c.id, x.pay])))).map(([id, t]) => ({ id, total: t }));
  const normal = lines.reduce((s, l) => s + l.normal * l.q, 0) + packs.reduce((s, x) => s + x.c.surprise_value, 0);
  const retail = lines.reduce((s, l) => s + l.retail * l.q, 0);
  const brand = (id) => cfg.brands.find((b) => b.id === id)?.name || "";
  const notes = [...used.filter((c) => c.gift).map((c) => `• ${c.name}: bonus gratuit ${giftText(c)}`),
    ...packs.map(({ c, pay }) => `• ${brand(c.brand)}: produse utile alese de noi, ${ron(c.surprise_value)} la preț de listă → ${ron(pay)} cu -${c.discount}% (incluse în total)`)];
  const shades = lines.some((l) => l.culoare) ? "\nNuanțe dorite: " : "";
  const reg = JSON.parse(localStorage.getItem("bf_reg_info") || "null");
  const who = reg ? `\nSalon: ${reg.salon} · CUI ${reg.cui}\nAdministrator: ${reg.admin} · ${reg.phone} · ${reg.email}\nLivrare: ${reg.address}${reg.postal ? `, cod poștal ${reg.postal}` : ""}` : "";
  const obsText = numbered(obs);
  const obsTop = obsText ? `\n\n*OBSERVAȚII (de citit):*\n${obsText}\n` : "";
  const text = `Bună ziua! Comandă ${cfg.title} ${cfg.subtitle}${obsText ? " · CU OBSERVAȚII" : ""}:${obsTop}${who}\n${lines.map((l) => `• ${l.q}× ${brand(l.brand)} – ${l.name} (${ron(l.unit)}/${l.per})`).join("\n")}${notes.length ? `\n${notes.join("\n")}` : ""}\nTotal: ${ron(total)} (TVA inclus) · economie ${ron(normal - total)}${shades}\n${STOCK}`;
  const greet = (t) => (contact.name ? `Bună ziua, ${contact.name}! ${t.replace(/^Bună ziua! /, "")}` : t);
  const wa = waLink(contact.phone, greet(text));
  const short = (r) => {
    const ps = proformaParts(r);
    const links = `Comanda completă în Excel (proforma ${r.no}${ps[0].sheets > 1 ? `, ${ps[0].sheets} foi a câte max. 40 de poziții` : ""}):\n${ps[0].xlsx}`;
    return greet(`Bună ziua! Comandă nouă ${cfg.title} ${cfg.subtitle}${obsText ? " · CU OBSERVAȚII" : ""}:${obsTop}${who}\nTotal: ${ron(total)} (TVA inclus) · ${pieces} buc.\n\n${links}`);
  };
  const send = (e) => {
    e.preventDefault();
    const ls = lines.map((l) => `${l.q}× ${brand(l.brand)} – ${l.name} (${ron(l.unit)}/${l.per})`.slice(0, 240)).slice(0, 400);
    const note = noteLines(obs).join("\n");
    const win = window.open("", "_blank");
    const go = (url) => { if (win && !win.closed) win.location.href = url; else window.location.href = url; };
    setPf("busy");
    makeProforma({ items: lines.map((l) => ({ key: l.id, q: l.q })), lead: localStorage.getItem("bf_lead_id") || "", agent: localStorage.getItem("bf_agent") || "", notes: note, preview: !!preview })
      .then((r) => { setPf(r); go(waLink(contact.phone, short(r))); window.dispatchEvent(new Event("bf-order-sent")); onOrder(total, parts, ls, r.id, note); })
      .catch(() => { setPf(null); go(wa); onOrder(total, parts, ls, "", note); });
  };
  const sendAgent = () => {
    if (pf === "busy" || pf?.id) return;
    setPf("busy");
    edit.freeze();
    agentCartProforma(edit.lead, lines.map((l) => ({ key: l.id, q: l.q })), noteLines(obs).join("\n"))
      .then((r) => { setPf(r); setObs(""); })
      .catch(() => { setPf(null); window.alert("Nu am putut genera proforma. Încearcă din nou."); });
  };
  const salonMsg = (r) => {
    const ps = proformaParts(r);
    return `Bună ziua${edit?.admin_name ? `, ${edit.admin_name}` : ""}! Am pregătit proforma pentru comanda dvs. ${cfg.title} ${cfg.subtitle}: total ${ron(total)} (TVA inclus), ${pieces} buc.\n\nProforma în Excel: ${ps[0].xlsx}\n\n${STOCK}`;
  };
  return (
    <>
      <button type="button" className="bf-cartbar" onClick={() => setOpen(true)} data-testid="bf-cart-bar">
        <ShoppingBag size={18} />
        <span><b data-testid="bf-cart-count">{pieces}</b> buc.</span>
        <strong data-testid="bf-cart-total">{ron(total)}</strong>
        <em>Vezi coșul</em>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bf-cart" data-testid="bf-cart-sheet">
          <SheetTitle className="bf-cart-title">Coșul tău Black Friday</SheetTitle>
          <SheetDescription className="bf-cart-sub">Prețuri pentru salon, cu TVA inclus</SheetDescription>
          <ul>
            {lines.map(({ id, q, brand: b, name, unit }) => (
              <li key={id} data-testid={`bf-cart-line-${id}`}>
                <div><small>{brand(b)}</small><span>{name}</span></div>
                {review || pf?.id ? <div className="bf-cart-q"><b data-testid={`bf-cart-q-${id}`}>{q} buc.</b></div> : (
                <div className="bf-cart-q">
                  <button type="button" onClick={() => setQty(id, q - 1)} data-testid={`bf-cart-minus-${id}`}>−</button>
                  <b>{q}</b>
                  <button type="button" onClick={() => setQty(id, q + 1)} data-testid={`bf-cart-plus-${id}`}>+</button>
                </div>
                )}
                <b className="bf-cart-sum">{ron(unit * q)}</b>
              </li>
            ))}
          </ul>
          {notes.map((x) => <p key={x} className="bf-cart-note">{x.slice(2)}</p>)}
          {unmet.map(({ c, missing }) => (
            <p key={c.id} className="bf-cart-warn" data-testid={`bf-cart-min-${c.id}`}>{c.name} ({campRule(c)}): {missing.join(" și ")}.</p>
          ))}
          <BFProfit lines={lines} />
          <BFNotes value={obs} onChange={setObs} />
          <p className="bf-stock" data-testid="bf-cart-stock">{STOCK}</p>
          <dl className="bf-cart-totals">
            <div><dt>Preț normal salon</dt><dd><s>{ron(normal)}</s></dd></div>
            <div><dt>Economisești</dt><dd data-testid="bf-cart-savings">{ron(normal - total)}</dd></div>
            {packs.map(({ c, pay }) => <div key={c.id} className="is-gold" data-testid={`bf-cart-pack-${c.brand}`}><dt>Produse alese de noi ({brand(c.brand)}, {ron(c.surprise_value)} listă)</dt><dd>{ron(pay)}</dd></div>)}
            {retail > 0 && <div className="is-gold"><dt>Valoare la client final</dt><dd>{ron(retail)}</dd></div>}
            <div className="is-total"><dt>Total Black Friday</dt><dd data-testid="bf-cart-sheet-total">{ron(total)}</dd></div>
          </dl>
          {edit ? (
            pf?.id ? null : review ? (
              <>
                <p className="bf-cart-review" data-testid="bf-cart-review">Verifică produsele și totalul. Proforma se face pe {edit.salon || "salon"}, cu datele lui de facturare.</p>
                <button type="button" className="bf-send" disabled={pf === "busy" || !!unmet.length} onClick={sendAgent} data-testid="bf-cart-agent-pf"><FileText size={16} /> Generează proforma</button>
                <button type="button" className="bf-clear" onClick={() => setReview(false)} data-testid="bf-cart-edit"><Pencil size={13} /> Modifică comanda</button>
              </>
            ) : <button type="button" className="bf-send" disabled={!!unmet.length} onClick={() => setReview(true)} data-testid="bf-cart-finish">Finalizează comanda</button>
          ) : !wa ? (
            <p className="bf-cart-sub" data-testid="bf-cart-no-phone">Numărul de WhatsApp pentru comenzi nu e setat încă.</p>
          ) : unmet.length ? (
            <button type="button" className="bf-send" disabled data-testid="bf-cart-send-disabled"><WhatsAppGlyph /> Completează pachetul ca să trimiți comanda</button>
          ) : !review ? (
            <button type="button" className="bf-send" onClick={() => setReview(true)} data-testid="bf-cart-finish">Finalizează comanda</button>
          ) : (
            <>
              <p className="bf-cart-review" data-testid="bf-cart-review">Verifică produsele și totalul. Poți modifica comanda până o trimiți.</p>
              <a className="bf-send" href={wa} target="_blank" rel="noopener noreferrer" onClick={send} data-testid="bf-cart-send">
                <WhatsAppGlyph /> Trimite comanda pe WhatsApp
              </a>
              <button type="button" className="bf-clear" onClick={() => setReview(false)} data-testid="bf-cart-edit"><Pencil size={13} /> Modifică comanda</button>
            </>
          )}
          {pf === "busy" && <p className="bf-cart-sub" data-testid="bf-proforma-busy"><Loader2 size={13} className="animate-spin" /> Pregătim factura proformă…</p>}
          {pf?.id && proformaParts(pf).map((p) => (
            <a key={p.no} className="bf-proforma" href={p.xlsx} data-testid="bf-proforma-download">
              <FileText size={16} /> Descarcă factura proformă {p.no} (Excel{p.tag})
            </a>
          ))}
          {edit && pf?.id && waLink(edit.phone, salonMsg(pf)) && <a className="bf-send" href={waLink(edit.phone, salonMsg(pf))} target="_blank" rel="noopener noreferrer" data-testid="bf-cart-agent-wa"><WhatsAppGlyph /> Trimite proforma salonului pe WhatsApp</a>}
          {edit && pf?.id && <a className="bf-clear" href="/?s=cosuri" data-testid="bf-cart-agent-done">Gata, înapoi la coșuri</a>}
          {!edit && <button type="button" className="bf-clear" onClick={() => { clear(); setObs(""); setOpen(false); }} data-testid="bf-cart-clear"><Trash2 size={13} /> Golește coșul</button>}
        </SheetContent>
      </Sheet>
    </>
  );
}
